import React, { useMemo } from 'react'

import * as styles from './ContentContainer.styles'

export enum ContainerAppearance {
  DEFAULT = 'default',
  EXTERNAL = 'external',
  DELAY = 'delay',
}

const AppearanceToStyleMapping: Record<ContainerAppearance, React.CSSProperties> = {
  [ContainerAppearance.DEFAULT]: styles.DefaultContainerStyle,
  [ContainerAppearance.EXTERNAL]: styles.ExternalContainerStyle,
  [ContainerAppearance.DELAY]: styles.DelayContainerStyle,
}

interface ContentContainerProps {
  appearance?: ContainerAppearance
  isEmpty?: boolean
  /**
   * Enables special styles for visually connect container with next element
   */
  connectedToNext?: boolean
  /**
   * Enables special styles for visually connect container with previous element
   */
  connectedToPrevious?: boolean
  style?: React.CSSProperties
  children?: React.ReactNode
  /**
   * Cache key for container rendering optimization
   * WARNING:
   * All elements with the same `sharedResourceId` will have same appearance and size
   */
  sharedResourceId?: string
}

/**
 * Unified container element for FlowChart content
 */
export const ContentContainer = ({
  appearance: presentation = ContainerAppearance.DEFAULT,
  isEmpty,
  connectedToNext,
  connectedToPrevious,
  style,
  children,
  sharedResourceId,
}: ContentContainerProps) => {
  const composedStyle = useMemo(() => {
    const externalStyle = style || {}
    const internalStyle = isEmpty
      ? styles.EmptyContainerStyle
      : AppearanceToStyleMapping[presentation]

    return {
      ...internalStyle,
      ...externalStyle,
      ...(connectedToNext ? styles.ConnectedToNextContainerStyle : {}),
      ...(connectedToPrevious ? styles.ConnectedToPreviousContainerStyle : {}),
    }
  }, [connectedToNext, connectedToPrevious, isEmpty, presentation, style])

  return (
    // eslint-disable-next-line
    // @ts-expect-error
    <view style={composedStyle} sharedResourceId={sharedResourceId}>
      {children}
    </view>
  )
}
